import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/healthcare.png";
import IndustryVerticalPage from "../../components/industry/industry-vertical-page";

const page = {
  title: "Healthcare Software Solutions, HIS, EMR & Patient Management | Avani Cimcon",
  description:
    "Healthcare IT solutions for hospitals, clinics and insurers. HIS, EMR, patient portals, SOA integration and AI enablement built on decades of enterprise delivery.",
  keywords:
    "Health care management system, healthcare it solutions, Solution for health care, IT solution for health care, health care software development company, software solution for health care industry, custom software development, IT consulting for health care, heath care technology consulting, software developer company, Custom Software Solutions for Health Care Industry, custom software services, custom health care software services, health care solutions software, hospital software, Healthcare Software solutions, patient management system, patient information system, Hospital Management Systems India, enterprise software engineering, legacy modernization, AI enablement, HIS, EMR, HL7, FHIR",
  canonical: "https://www.avanicimcon.com/healthcare",
  ogImage: "https://www.avanicimcon.com/images/banner/healthcare.png",
  hero: {
    eyebrow: "Industry Expertise",
    heading: "Healthcare",
    lead:
      "Healthcare systems need to combine clinical workflows, patient engagement, interoperability and compliance without breaking the operational systems already supporting the business.",
    image: BannerImg,
    tags: ["Hospital systems", "Patient management", "SOA integration", "Healthcare data exchange"],
    panelKicker: "Legacy systems. Modern enterprise delivery.",
    panelTitle: "Healthcare platforms for hospitals, clinics and healthcare insurers.",
    panelText:
      "We help healthcare organizations preserve core systems, modernize integration and improve digital services across the patient and provider journey.",
    stats: [
      { value: "HIS / EMR", label: "Core healthcare systems and records" },
      { value: "EAI", label: "SOA-based enterprise application integration" },
      { value: "HIPAA", label: "Security and compliance-aware delivery" },
    ],
    primaryCta: { label: "Talk to Us", href: "/contact-us" },
    secondaryCta: { label: "See IT Consulting Services", href: "/it-consulting-services" },
  },
  overview: {
    eyebrow: "Industry Overview",
    title: "Healthcare workflows that connect operations, patients and clinical systems",
    lead:
      "The healthcare industry is one of our major business focus areas, and the modernized page should reflect the depth of that experience while preserving long-tail keywords such as healthcare it solutions and hospital management systems.",
    paragraphs: [
      "Avani Cimcon Technologies provides expert custom software application development and Enterprise Application Integration services in healthcare industry verticals. Over the years we have worked on multiple projects related to hospitals, clinics, healthcare insurance and pharmaceutical companies.",
      "That experience has helped us build extensive healthcare business domain knowledge and niche services targeting this vertical. The modern framing is not generic AI consulting; it is enterprise software engineering for healthcare workflows that must continue operating safely.",
    ],
    callout: {
      title: "What this page should prove",
      text: "Healthcare buyers should see that we understand the systems, processes and constraints around hospital operations, patient data and interoperability.",
      items: [
        "Patient management and patient information systems",
        "Hospital information management systems",
        "Healthcare data exchange and integration",
      ],
    },
  },
  challenges: {
    eyebrow: "Industry Challenges",
    title: "Healthcare software has to balance clinical operations, compliance and interoperability",
    items: [
      {
        title: "Siloed clinical systems",
        text: "Hospitals and clinics often run on disconnected systems that need careful integration rather than replacement.",
      },
      {
        title: "Compliance and privacy",
        text: "HIPAA-aware delivery and secure data handling are essential for patient-facing and back-office workflows.",
      },
      {
        title: "Operational variability",
        text: "Healthcare workflows differ across hospitals, clinics, insurers and pharmaceutical organizations, so systems must be configurable.",
      },
    ],
  },
  systems: {
    eyebrow: "Enterprise Systems We Build & Modernize",
    title: "Healthcare platforms that support records, portals, scheduling and exchange",
    lead:
      "Preserve the terminology that has authority today, then frame it as part of a modernization and AI readiness story.",
    items: [
      {
        title: "Hospital Information Systems",
        text: "Hospital information management systems built to support operations and reporting.",
        href: "/it-consulting-services",
        linkLabel: "See solutions",
      },
      {
        title: "Patient Management Systems",
        text: "Patient management and patient information systems that can serve both online and offline scenarios.",
        href: "/it-consulting-services",
        linkLabel: "See solutions",
      },
      {
        title: "Enterprise Integration",
        text: "SOA-based EAI and healthcare data exchange using HL7/FHIR-aligned approaches.",
        href: "/it-consulting-services",
        linkLabel: "See solutions",
      },
      {
        title: "Customer Relationship Management",
        text: "CRM and portal experiences for hospitals and healthcare organizations.",
        href: "/it-consulting-services",
        linkLabel: "See solutions",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "Modernize healthcare systems by strengthening the integration layer",
    lead:
      "The healthcare page should explain how existing systems can be extended with safe integration and improved user experiences.",
    paragraphs: [
      "We have worked on analysis and expert system modules that formed parts of large HMO management systems, including work for hospital units managed by HMOs and modules that codify procedures used in different countries. That history shows real healthcare enterprise depth, not just marketing language.",
      "Our services allow healthcare organizations to realize administrative and medical cost savings, increased patient satisfaction and more streamlined business processes through a well-thought-out e-Health initiative.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Integration using SOA and enterprise application integration",
        "Patient and provider workflows that work online and offline",
        "Secure exchange of healthcare information",
        "Incremental modernization instead of disruptive replacement",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI should support care teams, operations and patients",
    lead:
      "Healthcare AI is most valuable when it augments existing systems and workflows.",
    items: [
      {
        title: "Clinical decision support",
        text: "Use AI to support triage, care planning and evidence discovery while respecting clinical workflows.",
      },
      {
        title: "Patient risk and outcome prediction",
        text: "Use analytics for readmission prediction, population health and operational planning.",
      },
      {
        title: "Document and note intelligence",
        text: "Use NLP and extraction to work with clinical notes, forms and exchange documents more efficiently.",
      },
    ],
  },
  experience: {
    eyebrow: "Our Experience in This Industry",
    title: "Healthcare delivery experience that spans hospitals, clinics and HMO systems",
    paragraphs: [
      "ACT has done projects in healthcare verticals like hospitals, clinics, healthcare insurance and pharmaceutical companies. That experience has helped the team develop extensive healthcare business domain knowledge and targeted services for the vertical.",
      "Projects have included analysis modules for large HMO management systems, expert system modules that codify procedures across countries, and custom account receivable and admissions software for nursing homes in the USA.",
    ],
    callout: {
      title: "Evidence to preserve",
      items: [
        "USA healthcare delivery proof",
        "HMO management system work",
        "Patient management and portal history",
        "Healthcare terminology already ranking in search",
      ],
    },
  },
  relatedSolutions: [
    { label: "IT Consulting Services", href: "/it-consulting-services", text: "Enterprise integration and modernization." },
    { label: "Quality Assurance", href: "/quality-assurance", text: "Safe delivery for healthcare systems." },
    { label: "DXchange", href: "/dxchange", text: "Enterprise data exchange and middleware." },
    { label: "About", href: "/about", text: "Heritage and enterprise credibility." },
  ],
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "Healthcare engineering grounded in delivery depth and operational realism",
    lead:
      "The page should emphasize experience, trust and long-term partnership over generic healthcare AI messaging.",
    items: [
      {
        title: "Domain expertise",
        text: "We understand hospital, clinic and insurer workflows, not just software categories.",
      },
      {
        title: "Integration-first thinking",
        text: "We can work with existing healthcare systems and improve the surrounding application landscape.",
      },
      {
        title: "Preserved keywords",
        text: "We retain healthcare it solutions, hospital software and patient management language that already has authority.",
      },
      {
        title: "Safe AI adoption",
        text: "We introduce AI in ways that support clinicians, operations and patients without weakening the core system.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Modernize healthcare systems without disrupting patient or provider operations",
    text:
      "Build on the systems you already trust and extend them with enterprise integration, modernization and practical AI enablement.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class Healthcare extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <IndustryVerticalPage {...page} />;
  }
}

export default Healthcare;
